/* do-trust.js — product-page trust & fit behaviour (v23 lane, batch 4)
   Owns: size-guide modal (focus trap), cm/in unit toggle (localStorage),
         UGC lightbox (arrow keys + Escape).
   Every entry point no-ops when its element is absent, so this file is safe to
   load on every template. Defer it; it never blocks render. */
(function () {
  'use strict';

  var UNIT_KEY = 'do:units';
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function $all(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function store(key, value) {
    try {
      if (value === undefined) return window.localStorage.getItem(key);
      window.localStorage.setItem(key, value);
    } catch (e) {
      /* private mode / blocked storage: fall back to session memory only */
      return null;
    }
    return value;
  }

  /* ------------------------------------------------------------------ *
   * Focus trap — shared by the modal and the lightbox
   * ------------------------------------------------------------------ */
  function trap(container) {
    var nodes = $all(FOCUSABLE, container).filter(function (n) {
      return n.offsetParent !== null || n === document.activeElement;
    });
    if (!nodes.length) return null;
    return {
      first: nodes[0],
      last: nodes[nodes.length - 1],
      handle: function (e) {
        if (e.key !== 'Tab') return;
        if (nodes.length === 1) { e.preventDefault(); nodes[0].focus(); return; }
        if (e.shiftKey && document.activeElement === nodes[0]) {
          e.preventDefault();
          nodes[nodes.length - 1].focus();
        } else if (!e.shiftKey && document.activeElement === nodes[nodes.length - 1]) {
          e.preventDefault();
          nodes[0].focus();
        }
      }
    };
  }

  /* ------------------------------------------------------------------ *
   * Overlay controller — open / close / restore focus
   * ------------------------------------------------------------------ */
  function Overlay(el, panelSelector) {
    this.el = el;
    this.panel = el.querySelector(panelSelector);
    this.opener = null;
    this.trap = null;
    this.onKey = this.onKey.bind(this);
  }

  Overlay.prototype.open = function (opener) {
    if (!this.el || !this.el.hasAttribute('hidden')) return;
    this.opener = opener || document.activeElement;
    this.el.removeAttribute('hidden');
    document.documentElement.style.overflow = 'hidden';
    this.trap = trap(this.panel || this.el);
    if (this.trap) this.trap.first.focus();
    document.addEventListener('keydown', this.onKey, true);
  };

  Overlay.prototype.close = function () {
    if (!this.el || this.el.hasAttribute('hidden')) return;
    this.el.setAttribute('hidden', '');
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', this.onKey, true);
    if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    this.opener = null;
  };

  Overlay.prototype.onKey = function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); this.close(); return; }
    if (this.trap) this.trap.handle(e);
    if (this.extraKey) this.extraKey(e);
  };

  /* ------------------------------------------------------------------ *
   * Size-guide modal
   * ------------------------------------------------------------------ */
  var overlays = {};

  function initModals(root) {
    $all('[data-do-modal]', root).forEach(function (el) {
      if (!el.id || overlays[el.id]) return;
      var o = new Overlay(el, '.do-modal__panel');
      overlays[el.id] = o;
      $all('[data-do-modal-close]', el).forEach(function (btn) {
        btn.addEventListener('click', function () { o.close(); });
      });
    });

    $all('[data-do-modal-open]', root).forEach(function (btn) {
      if (btn.dataset.doModalBound) return;
      btn.dataset.doModalBound = '1';
      btn.addEventListener('click', function () {
        var o = overlays[btn.getAttribute('data-do-modal-open')];
        if (o) o.open(btn);
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * cm / in unit toggle — persists in localStorage
   * Source of truth is always the authored centimetre value in data-do-cm.
   * ------------------------------------------------------------------ */
  function applyUnits(scope, unit) {
    var isIn = unit === 'in';

    $all('[data-do-cm]', scope).forEach(function (cell) {
      var cm = parseFloat(cell.getAttribute('data-do-cm'));
      if (isNaN(cm)) return;
      cell.textContent = isIn ? (Math.round((cm / 2.54) * 10) / 10).toFixed(1) : String(cm);
    });

    $all('[data-do-unit-label]', scope).forEach(function (l) { l.textContent = isIn ? 'in' : 'cm'; });
    $all('[data-do-unit-word]', scope).forEach(function (w) { w.textContent = isIn ? 'inches' : 'centimetres'; });

    $all('table[data-do-units]', scope).forEach(function (t) { t.setAttribute('data-do-units', isIn ? 'in' : 'cm'); });

    $all('[data-do-unit]', scope).forEach(function (btn) {
      var on = btn.getAttribute('data-do-unit') === unit;
      btn.setAttribute('aria-checked', on ? 'true' : 'false');
      btn.setAttribute('data-do-state', on ? 'selected' : 'default');
      btn.setAttribute('tabindex', on ? '0' : '-1');
    });

    var live = scope.querySelector('[data-do-unit-live]');
    if (live) live.textContent = isIn ? 'Measurements now shown in inches.' : 'Measurements now shown in centimetres.';
  }

  function initUnits(root) {
    var scopes = $all('[data-do-sizeguide]', root);
    if (!scopes.length) return;
    var saved = store(UNIT_KEY) === 'in' ? 'in' : 'cm';

    scopes.forEach(function (scope) {
      var buttons = $all('[data-do-unit]', scope);
      if (!buttons.length) return;
      applyUnits(scope, saved);

      buttons.forEach(function (btn, i) {
        btn.addEventListener('click', function () {
          var unit = btn.getAttribute('data-do-unit');
          store(UNIT_KEY, unit);
          $all('[data-do-sizeguide]').forEach(function (s) { applyUnits(s, unit); });
        });
        /* radiogroup keyboard semantics: arrows move and select */
        btn.addEventListener('keydown', function (e) {
          if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft' && e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
          e.preventDefault();
          var dir = (e.key === 'ArrowRight' || e.key === 'ArrowDown') ? 1 : -1;
          var next = buttons[(i + dir + buttons.length) % buttons.length];
          next.focus();
          next.click();
        });
      });
    });
  }

  /* ------------------------------------------------------------------ *
   * UGC lightbox — arrows + Escape, focus returns to the tile
   * ------------------------------------------------------------------ */
  function initLightboxes(root) {
    $all('[data-do-lightbox]', root).forEach(function (el) {
      var key = el.getAttribute('data-do-lightbox');
      if (!key || overlays['lb:' + key]) return;

      var tiles = $all('[data-do-lightbox-open="' + key + '"]');
      if (!tiles.length) return;

      var img = el.querySelector('[data-do-lightbox-img]');
      var cap = el.querySelector('[data-do-lightbox-caption]');
      var live = el.querySelector('[data-do-lightbox-live]');
      var o = new Overlay(el, '.do-lightbox__panel');
      var index = 0;
      overlays['lb:' + key] = o;

      function show(i) {
        index = (i + tiles.length) % tiles.length;
        var t = tiles[index];
        if (img) {
          img.setAttribute('src', t.getAttribute('data-do-src') || '');
          img.setAttribute('alt', t.getAttribute('data-do-alt') || '');
        }
        if (cap) {
          var credit = t.getAttribute('data-do-credit');
          cap.textContent = (credit ? credit + ' · ' : '') + 'Photo ' + (index + 1) + ' of ' + tiles.length;
        }
        if (live) live.textContent = 'Photo ' + (index + 1) + ' of ' + tiles.length;
      }

      tiles.forEach(function (t, i) {
        t.addEventListener('click', function () { show(i); o.open(t); });
      });

      $all('[data-do-lightbox-close]', el).forEach(function (b) {
        b.addEventListener('click', function () { o.close(); });
      });
      var prev = el.querySelector('[data-do-lightbox-prev]');
      var next = el.querySelector('[data-do-lightbox-next]');
      if (prev) prev.addEventListener('click', function () { show(index - 1); });
      if (next) next.addEventListener('click', function () { show(index + 1); });

      o.extraKey = function (e) {
        if (e.key === 'ArrowLeft') { e.preventDefault(); show(index - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); show(index + 1); }
      };

      /* focus must return to the tile that was clicked, not the first tile */
      var baseClose = o.close.bind(o);
      o.close = function () {
        var t = tiles[index];
        if (t) o.opener = t;
        baseClose();
      };
    });
  }

  /* ------------------------------------------------------------------ *
   * Retry buttons — present so a later review/QA/UGC app has a hook.
   * With no backend wired they simply re-dispatch a namespaced event that
   * nothing listens to yet; they never fake a successful load.
   * ------------------------------------------------------------------ */
  function initRetries(root) {
    [['[data-do-reviews-retry]', 'do:reviews:retry'],
     ['[data-do-qa-retry]', 'do:qa:retry'],
     ['[data-do-ugc-retry]', 'do:ugc:retry']].forEach(function (pair) {
      $all(pair[0], root).forEach(function (btn) {
        if (btn.dataset.doRetryBound) return;
        btn.dataset.doRetryBound = '1';
        btn.addEventListener('click', function () {
          document.dispatchEvent(new CustomEvent(pair[1], { bubbles: true, detail: { trigger: btn } }));
        });
      });
    });
  }

  function init(root) {
    var scope = root || document;
    initModals(scope);
    initUnits(scope);
    initLightboxes(scope);
    initRetries(scope);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  /* theme editor: sections re-render without a page load */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:section:unload', function (e) {
    $all('[data-do-modal],[data-do-lightbox]', e.target).forEach(function (el) {
      var id = el.id || ('lb:' + el.getAttribute('data-do-lightbox'));
      if (overlays[id]) { overlays[id].close(); delete overlays[id]; }
    });
  });
})();
